<template>
    <product-card-layout
        class="activity-card"
        :thumb="product.mainImage"
        :name="product.info.name"
        :product-link="productLink"
        :offers-count="product.offers && product.offers.length"
        :is-recommended="product.recommended"
        :_expanded-count="expanded"
        :_expanded-limit="expandedLimit"
        @change-expanded-count="expanded = $event"
        @change-expanded-limit="expandedLimit = $event"
    >
        <template v-slot:wishlist>
            <wishlist-like
                :info="{
                    productType: 'activity',
                    mainImage: product.mainImage,
                    shortDescription: product.shortDescription,
                    info: {
                        name: product.info.name,
                        id: product.info.id,
                    },
                    offers: [],
                    searchRequest,
                }"
                :query="{id: product.info.id, productType: 'activity'}"
                :person="person"
                :product-runtime-store="productRuntimeStore"
                :in-wishlist="product.wishlist"
            />
        </template>
        <template v-slot>
            <div>
                <div>
                    <nuxt-link
                        v-if="!isAddOn"
                        no-prefetch
                        :title="product.info.name"
                        :to="productLink"
                        class="activity-card__title me-2 font-weight-medium text-decoration-none"
                    >
                        {{ product.info.name }}
                    </nuxt-link>
                    <span v-else class="activity-card__title me-2 font-weight-medium text-decoration-none">
                        {{ product.info.name }}
                    </span>
                    <v-chip v-if="product.type && product.type !== 'UNKNOWN'" label size="x-small">
                        {{ $t(`filters_activityType.${product.type}`) }}
                    </v-chip>
                </div>
                <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" class="mb-2" />
                <template v-if="activityVenue">
                    <VenueLabel
                        :venue-name="activityVenue.name"
                        :address="activityVenue.address"
                        :hide-show-on-map-button="!activityVenue.latitude || !activityVenue.longitude"
                        is-show-icon
                        @click="venueMapModal = true"
                    />
                    <client-only>
                        <v-dialog v-model="venueMapModal" :max-width="500">
                            <map-location
                                v-if="venueMapModal"
                                :latitude="activityVenue?.latitude"
                                :longitude="activityVenue?.longitude"
                                :height="`500px`"
                            />
                        </v-dialog>
                    </client-only>
                </template>
                <div
                    v-if="product.shortDescription"
                    class="short-description"
                    v-html="$filters.stripHtml(product.shortDescription)"
                />
                <activity-duration
                    v-if="product.offers[0]"
                    :days="product.offers[0].durationDays"
                    :time="product.offers[0].durationTime"
                    class="mt-auto"
                />
                <transfer-driver-languages v-if="langCodes" class="mt-1" :lang-codes="langCodes" />
                <div class="activity-card__slot-wrapper"><slot /></div>
            </div>
        </template>
        <template v-if="minPriceOffer" v-slot:priceCol>
            <slot name="price" :offer="minPriceOffer" :offer-key="minPriceOffer.offerKey">
                <template v-if="!minPriceOffer.soldOut">
                    <activity-card-price-block :offer="minPriceOffer" />
                    <v-btn variant="flat" block color="primary" class="activity-card__book my-2" :to="productLink">
                        {{ $t('book') }}
                    </v-btn>
                </template>
            </slot>
            <div class="activity-card__show text-primary cursor-pointer text-no-wrap" @click="toggleOffers">
                <span>
                    {{ expanded ? $t('hide') : $t('show') }}
                </span>
                <v-icon color="primary">
                    {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
            </div>
        </template>
        <template v-if="product.offers.length" v-slot:offers>
            <div v-if="expanded" class="activity-card__offers mt-3">
                <v-row>
                    <v-col cols="12" md="4" class="px-6 mb-3">
                        <date-filter-search v-model="dateFilter" outlined :dates-for-filter="datesForFilter" />
                    </v-col>
                </v-row>
                <activity-grouped-offers :sorted-filtered-offers="sortedFilteredOffers" :hide-price="hidePrice">
                    <template v-slot:price="{offer, offerKey}">
                        <slot name="price" :offer="offer" :offer-key="offerKey" />
                    </template>
                </activity-grouped-offers>
                <template v-if="groupOffersBy === 'none'">
                    <activity-offer-card
                        v-for="(offer, offerIndex) in sortedFilteredSlicedOffers"
                        :key="offerIndex"
                        :offer="offer"
                        :full-card="false"
                        :data-cy="`activity-offer-card-${offerIndex}`"
                    >
                        <template v-slot:price>
                            <slot name="price" :offer="offer" :offer-key="offer.offerKey" />
                        </template>
                    </activity-offer-card>
                    <v-list-item
                        v-if="expanded === expandedLimit && sortedFilteredOffers.length > expandedLimit"
                        density="compact"
                        class="d-flex justify-center"
                        @click.stop="expand"
                    >
                        <v-icon>mdi-chevron-down</v-icon>
                    </v-list-item>
                </template>
            </div>
        </template>
    </product-card-layout>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import DiscountLabel from '@/components/snippets/DiscountLabel.vue'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import ActivityOfferCard from '@/components/activities/info/ActivityOfferCard.vue'
    import ActivityGroupedOffers from '@/components/activities/search/offers/ActivityGroupedOffers.vue'
    import ActivityCardPriceBlock from '~src/components/activities/search/offers/activityCardPriceBlock.src'
    import DateFilterSearch from '@/components/snippets/DateFilterSearch.vue'
    import ProductCardLayout from '~/components/search/ProductCardLayout.vue'
    import ActivityDuration from '@/components/activities/search/offers/ActivityDuration.vue'
    import WishlistLike from '@/components/search/offers/WishlistLike.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import ProductCardLayoutMobile from '@/components/search/ProductCardLayoutMobile.vue'
    import {minPriceOffer} from '~/utils/sort-helpers'
    import TransferDriverLanguages from '~/components/snippets/TransferDriverLanguages.vue'
    import VenueLabel from '~/components/activities/snippets/VenueLabel.vue'
    import MapLocation from '~/components/info/MapLocation.vue'

    @Component({
        components: {
            MapLocation,
            VenueLabel,
            TransferDriverLanguages,
            ProductCardLayoutMobile,
            DateFilterSearch,
            WishlistLike,
            ActivityDuration,
            ActivityGroupedOffers,
            CommissionAmount,
            DiscountLabel,
            ActivityOfferCard,
            ActivityCardPriceBlock,
            ProductCardLayout,
            SupplierNameLabel,
        },
    })
    export class ActivityCard extends Vue {
        @Prop({required: true}) product
        @Prop({required: true, type: Object}) searchRequest
        @Prop({default: false, type: Boolean}) hidePrice
        @Prop({default: false, type: Boolean}) isAddOn

        expanded = 0
        expandedLimit = 3
        imgError = false
        dateFilter = null
        venueMapModal = null

        toggleOffers() {
            let expanded = 0
            if (!this.expanded) {
                expanded = this.expandedLimit
            }
            this.expanded = expanded
        }

        expand() {
            this.expanded = this.product.offers.length
            this.expandedLimit = this.product.offers.length
        }

        get datesForFilter() {
            return this.product.offers.map(o => o.date)
        }

        get sortedFilteredOffers() {
            let offers = [...this.product.offers]
            if (this.dateFilter) {
                offers = offers.filter(offer => offer.date === this.dateFilter)
            }
            return offers
        }

        get sortedFilteredSlicedOffers() {
            return this.sortedFilteredOffers.slice(0, this.expanded)
        }

        get minPriceOffer() {
            return minPriceOffer(this.product)
        }

        get groupOffersBy() {
            return activitiesStore.groupOffersBy
        }

        get sortKey() {
            return activitiesStore.sortFnName
        }

        get productLink() {
            return activitiesRuntimeStore.infoPageLink(this.product, this.searchRequest)
        }

        get productRuntimeStore() {
            return activitiesRuntimeStore
        }

        get person() {
            return authStore.person
        }

        get isTO1() {
            return authStore.isTO1
        }

        get langCodes() {
            return this.product.offers.reduce((arr, e) => {
                e.languages
                    ?.map(el => el.langCode)
                    .forEach(lang => {
                        if (!arr.includes(lang)) {
                            arr.push(lang)
                        }
                    })
                return arr
            }, [])
        }

        get activityVenue() {
            return this.product?.venue
        }
    }

    export default ActivityCard
</script>

<style scoped lang="scss">
    .short-description {
        white-space: normal;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
